import { z } from 'zod';

/**
 * The permission catalogue (doc 06 section 4.2).
 *
 * This is the single source of truth. The migration that seeds the
 * `permissions` table is generated from this list, and CI checks that every
 * registered route declares a permission that appears here -- a route with no
 * declared permission fails the build (doc 05 section 1, rule 5).
 */

export const SCOPE_LEVELS = ['platform', 'reseller', 'tenant', 'site'] as const;
export const ScopeLevelSchema = z.enum(SCOPE_LEVELS);
export type ScopeLevel = z.infer<typeof ScopeLevelSchema>;

export interface PermissionDef {
  key: string;
  resource: string;
  verb: string;
  description: string;
  min_scope: ScopeLevel;
}

const p = (
  key: string,
  description: string,
  min_scope: ScopeLevel = 'tenant',
): PermissionDef => {
  const [resource, ...rest] = key.split(':');
  return { key, resource: resource ?? key, verb: rest.join(':'), description, min_scope };
};

export const PERMISSIONS: readonly PermissionDef[] = [
  // tenancy
  p('tenant:read', 'View tenant details'),
  p('tenant:write', 'Modify tenant settings'),
  p('tenant:create', 'Create tenants', 'reseller'),
  p('tenant:delete', 'Delete tenants', 'reseller'),
  p('tenant:suspend', 'Suspend or resume a tenant', 'reseller'),
  p('reseller:read', 'View resellers', 'reseller'),
  p('reseller:write', 'Modify resellers', 'platform'),
  p('site:read', 'View sites'),
  p('site:write', 'Modify sites'),
  p('branding:write', 'Modify branding', 'reseller'),

  // identity
  p('user:read', 'View users'),
  p('user:invite', 'Invite users'),
  p('user:write', 'Modify users'),
  p('user:delete', 'Delete users'),
  p('role:read', 'View roles'),
  p('role:write', 'Modify roles'),
  p('membership:write', 'Assign roles to users'),
  p('apikey:read', 'View API keys'),
  p('apikey:write', 'Create or revoke API keys'),
  p('sso:write', 'Configure SSO'),
  p('impersonate:start', 'Start an impersonation session', 'reseller'),

  // telephony
  p('extension:read', 'View extensions'),
  p('extension:write', 'Create or modify extensions'),
  p('extension:delete', 'Delete extensions'),
  p('extension:credentials:reveal', 'Reveal a SIP password'),
  p('device:read', 'View devices'),
  p('device:write', 'Modify devices'),
  p('device:provision', 'Provision devices'),
  p('handsetmodel:write', 'Maintain the catalogue of supported handsets', 'platform'),
  p('ringgroup:write', 'Modify ring groups'),
  p('queue:read', 'View queues'),
  p('queue:write', 'Modify queues'),
  p('queue:agent:manage', 'Manage queue agents'),
  p('voicemail:read', 'Read voicemail'),
  p('voicemail:manage', 'Manage voicemail boxes'),
  p('featurecode:write', 'Modify feature codes'),
  // A seat's package sets its features AND its monthly charge, so choosing one
  // is the provider's decision, never the customer's.
  p('extension:package:write', 'Change the package or paid add-ons of an extension', 'reseller'),

  // numbering
  p('number:read', 'View numbers'),
  p('number:assign', 'Assign numbers'),
  p('number:release', 'Release numbers'),
  p('number:order', 'Order numbers', 'reseller'),
  p('port:read', 'View ports'),
  p('port:write', 'Manage ports'),
  p('inboundroute:write', 'Modify inbound routes'),

  // routing
  p('outboundroute:read', 'View outbound routes'),
  p('outboundroute:write', 'Modify outbound routes'),
  // Routing an account on OUR trunks is the provider's decision: which carrier a
  // call leaves by is a cost and a quality commitment we make. A customer on its
  // own trunks (BYOT) routes with outboundroute:write alone.
  p('outboundroute:manage', 'Change how an account on our trunks routes outbound calls', 'reseller'),
  p('dialpermission:write', 'Modify dial permissions'),

  // carriers
  p('carrier:read', 'View carriers', 'platform'),
  p('carrier:write', 'Modify carriers', 'platform'),
  p('trunk:read', 'View trunks', 'platform'),
  p('trunk:write', 'Modify trunks', 'platform'),
  // What a CUSTOMER may see of the trunks its calls use: the carrier's name and
  // whether it is up and registered -- never hosts or credentials, which
  // trunk:read shows. Deliberately NOT under trunk:, because the customer roles
  // below drop that whole prefix.
  p('connectivity:read', 'View the trunks this account uses and whether they are up'),
  // Bring your own trunk: the customer configures its OWN carrier account.
  // Only does anything once the provider has switched the account to BYOT.
  p('connectivity:write', 'Configure the account’s own trunk (bring your own trunk)'),

  // flows
  p('flow:read', 'View call flows'),
  p('flow:write', 'Modify call flows'),
  p('flow:publish', 'Publish call flows'),
  p('flow:template:manage', 'Manage flow templates', 'reseller'),

  // fleet
  p('node:read', 'View nodes', 'platform'),
  p('node:write', 'Modify nodes', 'platform'),
  p('node:command', 'Send node commands', 'platform'),
  p('node:drain', 'Drain a node', 'platform'),

  // cdr
  p('cdr:read', 'View call records'),
  p('cdr:export', 'Export call records'),
  p('recording:read', 'Play call recordings'),
  p('recording:delete', 'Delete call recordings'),

  // billing
  p('billing:read', 'View billing'),
  // Mode (prepaid or postpaid), rate deck, VAT and billing day are commercial
  // terms: a customer holding this could move itself onto a cheaper rate deck.
  p('billing:write', 'Modify billing terms', 'reseller'),
  p('ratedeck:read', 'View rate decks', 'reseller'),
  p('ratedeck:write', 'Modify rate decks', 'reseller'),
  p('invoice:read', 'View invoices'),
  p('invoice:issue', 'Issue invoices', 'reseller'),
  p('payment:record', 'Record payments', 'reseller'),
  // Buying airtime by card (docs/28 B1). Its own key rather than billing:read:
  // the read-only role holds every ':read', and a read-only user starting
  // payments would not be read-only.
  p('payment:make', 'Pay for airtime', 'tenant'),
  p('balance:adjust', 'Adjust balances', 'reseller'),
  p('creditlimit:write', 'Set credit limits', 'reseller'),

  // products
  p('product:write', 'Modify the product catalogue: packages, features and prices', 'platform'),

  // reporting
  p('report:read', 'View reports'),
  p('report:schedule', 'Schedule reports'),

  // audit
  p('audit:read', 'View the audit log'),

  // notifications
  p('webhook:read', 'View webhooks'),
  p('webhook:write', 'Modify webhooks'),
] as const;

export const PERMISSION_KEYS = PERMISSIONS.map((x) => x.key);
export type PermissionKey = (typeof PERMISSIONS)[number]['key'];

/**
 * Actions requiring MFA re-verification within 5 minutes (doc 06 section 4.4).
 * An impersonated session can never satisfy step-up.
 */
export const STEP_UP_PERMISSIONS: readonly string[] = [
  'extension:credentials:reveal',
  'apikey:write',
  'billing:write',
  // What a call costs is billing too: creating and changing billing must not be
  // callable from the public (owner, 15 September 2026).
  'ratedeck:write',
  // An invoice is a legal document, and a recorded payment settles money owed.
  'invoice:issue',
  'payment:record',
  'balance:adjust',
  'creditlimit:write',
  'sso:write',
  'impersonate:start',
  'node:drain',
];

/** Built-in roles (doc 06 section 4.3). Seeded, not user-editable. */
export interface RoleDef {
  key: string;
  name: string;
  scope_level: ScopeLevel;
  description: string;
  permissions: readonly string[] | '*';
}

const READ_ONLY = PERMISSION_KEYS.filter((k) => k.endsWith(':read'));

export const BUILTIN_ROLES: readonly RoleDef[] = [
  {
    key: 'platform_admin',
    name: 'Platform Admin',
    scope_level: 'platform',
    description: 'Full control of the platform. MFA mandatory.',
    permissions: '*',
  },
  {
    key: 'platform_support',
    name: 'Platform Support',
    scope_level: 'platform',
    description: 'Read everything, impersonate, diagnose. No billing writes, no destructive actions.',
    permissions: [...READ_ONLY, 'impersonate:start', 'node:command'],
  },
  {
    key: 'platform_finance',
    name: 'Platform Finance',
    scope_level: 'platform',
    description: 'Billing, rate decks, invoices and payments. Read-only elsewhere.',
    permissions: [
      ...READ_ONLY, 'billing:write', 'ratedeck:write', 'invoice:issue',
      'payment:record', 'balance:adjust', 'creditlimit:write',
    ],
  },
  {
    key: 'reseller_admin',
    name: 'Reseller Admin',
    scope_level: 'reseller',
    description: 'Full control of own tenants, branding and retail rates. MFA mandatory.',
    permissions: PERMISSION_KEYS.filter(
      (k) => !k.startsWith('carrier:') && !k.startsWith('trunk:') && !k.startsWith('node:') && k !== 'reseller:write',
    ),
  },
  {
    key: 'reseller_support',
    name: 'Reseller Support',
    scope_level: 'reseller',
    description: 'Read plus support actions across own tenants.',
    permissions: [...READ_ONLY, 'impersonate:start'],
  },
  {
    key: 'tenant_admin',
    name: 'Tenant Admin',
    scope_level: 'tenant',
    description: 'Everything within the tenant except platform configuration.',
    permissions: PERMISSION_KEYS.filter(
      (k) =>
        !k.startsWith('carrier:') && !k.startsWith('trunk:') && !k.startsWith('node:') &&
        !k.startsWith('reseller:') && !k.startsWith('ratedeck:') && k !== 'tenant:create' &&
        k !== 'tenant:delete' && k !== 'tenant:suspend' && k !== 'impersonate:start',
    ),
  },
  {
    key: 'tenant_manager',
    name: 'Tenant Manager',
    scope_level: 'tenant',
    description: 'Extensions, devices, flows, queues and reports. No billing, no user management.',
    permissions: [
      'tenant:read', 'site:read', 'extension:read', 'extension:write', 'extension:delete',
      'device:read', 'device:write', 'device:provision', 'ringgroup:write',
      'queue:read', 'queue:write', 'queue:agent:manage', 'voicemail:read', 'voicemail:manage',
      'number:read', 'inboundroute:write', 'flow:read', 'flow:write', 'flow:publish',
      'cdr:read', 'cdr:export', 'recording:read', 'report:read',
      // "Are calls getting out?" is the first question when they are not.
      'connectivity:read',
    ],
  },
  {
    key: 'tenant_finance',
    name: 'Tenant Finance',
    scope_level: 'tenant',
    description: 'Invoices, balance and usage. No telephony configuration.',
    permissions: ['tenant:read', 'billing:read', 'invoice:read', 'report:read', 'cdr:read', 'payment:make'],
  },
  {
    key: 'tenant_user',
    name: 'Tenant User',
    scope_level: 'tenant',
    description: 'Own extension settings, voicemail and call history.',
    permissions: ['tenant:read', 'extension:read', 'voicemail:read', 'cdr:read'],
  },
  {
    key: 'read_only',
    name: 'Read Only',
    scope_level: 'tenant',
    description: 'Read access only.',
    permissions: READ_ONLY,
  },
] as const;

/** Broadest first: a holder may hold permissions whose min_scope is its own level or narrower. */
const SCOPE_RANK: Record<ScopeLevel, number> = { platform: 0, reseller: 1, tenant: 2, site: 3 };

/**
 * Whether a holder at `level` may hold `key` at all.
 *
 * `min_scope` was recorded for every permission and enforced nowhere. The
 * tenant admin role is built as "everything except", so it quietly included
 * balance:adjust, creditlimit:write and billing:write: a customer's admin could
 * credit their own balance, raise their own credit limit or move onto a cheaper
 * rate deck. Applied when roles are seeded AND when a permission set is loaded
 * (apps/api/src/auth/permissions.ts and auth/apikeys.ts), so neither a role
 * definition nor a stale grant in the database can widen it.
 */
export function permissionAllowedAt(key: string, level: ScopeLevel): boolean {
  const def = PERMISSIONS.find((x) => x.key === key);
  return def !== undefined && SCOPE_RANK[def.min_scope] >= SCOPE_RANK[level];
}

export function permissionsForRole(role: RoleDef): readonly string[] {
  const keys = role.permissions === '*' ? PERMISSION_KEYS : role.permissions;
  return keys.filter((k) => permissionAllowedAt(k, role.scope_level));
}
